import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Play, Pause, Clock, Database, Trash2, Key } from "lucide-react";
import { toArabicNumbers } from "@/utils/arabicNumbers";
import { ActionIcon } from "@mantine/core";
import { IconBuildings } from "@tabler/icons-react";

interface School {
  id: string;
  subscription_id: string;
  school_name: string;
  email: string;
  phone?: string;
  status: "active" | "inactive" | "expired";
  subscription_end?: string;
  students_count: number;
  created_at: string;
  updated_at: string;
}

interface SchoolManagementTableProps {
  schools: School[];
  onToggleStatus: (schoolId: string) => void;
  onExtendSubscription: (school: School) => void;
  onViewSchoolData: (school: School) => void;
  onDeleteSchool: (school: School) => void;
  onViewCredentials: (school: School) => void;
  onSchoolNav: (school: School) => void;
}

const SchoolManagementTable = ({
  schools,
  onToggleStatus,
  onExtendSubscription,
  onViewSchoolData,
  onDeleteSchool,
  onViewCredentials,
  onSchoolNav,
}: SchoolManagementTableProps) => {
  const { toast } = useToast();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "inactive":
        return "bg-gray-100 text-gray-800";
      case "expired":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "active":
        return "نشط";
      case "inactive":
        return "متوقف";
      case "expired":
        return "منتهي الصلاحية";
      default:
        return "غير محدد";
    }
  };

  const handleToggleStatus = (schoolId: string) => {
    onToggleStatus(schoolId);
  };

  const handleViewSchoolData = (school: School) => {
    onViewSchoolData(school);
    toast({
      title: "تم فتح بيانات المدرسة",
      description: `عرض بيانات ${school.school_name}`,
    });
  };

  const handleDeleteSchool = (school: School) => {
    onDeleteSchool(school);
  };

  const handleViewCredentials = (school: School) => {
    onViewCredentials(school);
  };

  if (schools.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        لا توجد مدارس مسجلة حتى الآن
      </div>
    );
  }

  return (
    <Table className="rounded-lg overflow-hidden border border-gray-200/50 shadow-sm backdrop-blur-sm bg-white/80">
      <TableHeader>
        <TableRow className="bg-gradient-to-r from-school-navy/90 to-school-blue/90 hover:bg-gradient-to-r hover:from-school-navy hover:to-school-blue border-b">
          <TableHead className="text-center text-white font-semibold text-xs border-r border-white/20">
            الإجراءات
          </TableHead>
          <TableHead className="text-center text-white font-semibold text-xs border-r border-white/20">
            تأريخ الإنشاء{" "}
          </TableHead>
          <TableHead className="text-center text-white font-semibold text-xs border-r border-white/20">
            الحالة
          </TableHead>
          <TableHead className="text-center text-white font-semibold text-xs border-r border-white/20">
            عدد الطلاب
          </TableHead>
          <TableHead className="text-center text-white font-semibold text-xs">
            اسم المدرسة
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {schools.map((school, index) => (
          <TableRow
            key={school.id}
            className={
              index % 2 === 0 ? "bg-gray-50" : "bg-white hover:bg-gray-100"
            }
          >
            <TableCell className="text-center border-r border-gray-200/50">
              <div className="flex items-center justify-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToggleStatus(school.id)}
                  className={
                    school.status === "active"
                      ? "text-red-600"
                      : "text-green-600"
                  }
                  title={
                    school.status === "active"
                      ? "إلغاء تنشيط المدرسة"
                      : "تنشيط المدرسة"
                  }
                >
                  {school.status === "active" ? (
                    <Pause className="h-4 w-4" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                </Button>
                {/* <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onExtendSubscription(school)}
                  className="text-blue-600"
                  title="تمديد الاشتراك"
                >
                  <Clock className="h-4 w-4" />
                </Button> */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => (school ? handleViewSchoolData(school) : null)}
                  className="text-purple-600"
                  title="عرض بيانات المدرسة"
                >
                  <Database className="h-4 w-4" />
                </Button>{" "}
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={() => (school ? onSchoolNav(school) : null)}
                  className="text-purple-600"
                  title="إدارة المدرسة"
                >
                  <IconBuildings className="h-4 w-4" />
                </ActionIcon>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleViewCredentials(school)}
                  className="text-orange-600"
                  title="توليد كلمة سر جديدة للمدير"
                >
                  <Key className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteSchool(school)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  title="حذف المدرسة"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </TableCell>
            <TableCell className="text-center border-r border-gray-200/50">
              {school.created_at
                ? new Date(school.created_at).toLocaleDateString("ar-SA")
                : "غير محدد"}
            </TableCell>
            <TableCell className="text-center border-r border-gray-200/50">
              <Badge className={getStatusColor(school.status)}>
                {getStatusLabel(school.status)}
              </Badge>
            </TableCell>
            <TableCell className="text-center border-r border-gray-200/50">
              {toArabicNumbers(school.students_count)}
            </TableCell>
            <TableCell className="font-medium text-center">
              {school.school_name}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default SchoolManagementTable;
